import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useTenant } from "../hooks/useTenant";
import { useAuditLookups } from "../hooks/useAuditLookups";
import { supabase } from "../lib/supabase";
import { EmptyState } from "../components/EmptyState";
import { SkeletonLine } from "../components/Skeleton";
import { colors, fontFamilyMono, fontSize, fontWeight, radius, shadow, space } from "../lib/tokens";
import { labelForAuditAction, labelForAuditRow } from "../lib/auditActionLabels";
import { auditEntityOptions, labelForAuditEntity } from "../lib/auditEntityLabels";
import { AUDIT_ORIGIN_OPTIONS, labelForAuditOrigin } from "../lib/auditOriginLabels";
import { collectAuditRefs, summarizeAuditRow } from "../lib/auditSummary";
import type { AuditLogRow, AuditRefs, AuditSummary, Lookups } from "../lib/auditSummary";
import { revokedGrantLastDay } from "../lib/auditSummary/billing";

const PAGE_SIZE = 50;
const PT_BR = new Intl.Collator("pt-BR");
const OPTION_PAGE = 1000;
const OPTION_MAX_PAGES = 50;

interface LogFilters {
  action: string;
  origin: string;
  entity: string;
  dateFrom: string;
  dateTo: string;
}

interface LogPage {
  /** Identifies the request this page answers; a mismatch means it is still loading. */
  key: string;
  rows: AuditLogRow[];
  total: number;
  unread: boolean;
}

const NO_FILTERS: LogFilters = { action: "", origin: "", entity: "", dateFrom: "", dateTo: "" };

export function TenantLogPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { tenant, loading: tenantLoading } = useTenant(slug);
  const tenantId = tenant?.id ?? null;
  const locale = tenant?.locale ?? "pt-BR";

  const [filterValues, setFilterValues] = useState<LogFilters>(NO_FILTERS);
  const [staffFilter, setStaffFilter] = useState("");
  const [page, setPage] = useState(0);
  const [result, setResult] = useState<LogPage | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [actionOptions, setActionOptions] = useState<string[]>([]);
  const [entityOptions, setEntityOptions] = useState<{ value: string; label: string }[]>([]);
  const requestSeq = useRef(0);

  const requestKey = useMemo(() => JSON.stringify([tenantId, filterValues, page]), [tenantId, filterValues, page]);
  const loading = result?.key !== requestKey;
  const logs = useMemo(() => result?.rows ?? [], [result]);
  const totalCount = result?.total ?? 0;
  const unread = result?.unread ?? false;

  const refs = useMemo<AuditRefs>(() => {
    const collected = collectAuditRefs(logs);
    const staffIds = logs.map((l) => l.staff_id).filter((id): id is string => !!id);
    return { ...collected, profileIds: Array.from(new Set([...collected.profileIds, ...staffIds])) };
  }, [logs]);
  const lookups = useAuditLookups(tenantId, refs);

  useEffect(() => {
    if (!tenantId) return;
    let active = true;
    void fetchFilterOptions(tenantId).then(({ actions, entities }) => {
      if (!active) return;
      setActionOptions(actions);
      setEntityOptions(entities);
    });
    return () => {
      active = false;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    // Only the latest request may land; an older page or filter answer is dropped (KTD14).
    const seq = ++requestSeq.current;
    void fetchLogs(tenantId, filterValues, page).then((fetched) => {
      if (seq !== requestSeq.current) return;
      setResult({ key: requestKey, ...fetched });
    });
  }, [tenantId, filterValues, page, requestKey]);

  const setFilter = (name: keyof LogFilters, value: string) => {
    setFilterValues((prev) => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const filteredLogs = useMemo(() => {
    if (!staffFilter) return logs;
    const needle = staffFilter.toLowerCase();
    return logs.filter((l) => {
      if (!l.staff_id) return false;
      return (staffName(lookups, l.staff_id) ?? "").toLowerCase().includes(needle);
    });
  }, [logs, staffFilter, lookups]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (tenantLoading || !tenant) {
    return <div style={{ color: colors.stone }}>Carregando...</div>;
  }

  let countText: string;
  if (loading) countText = "—";
  else if (unread) countText = "Não consegui ler o log agora.";
  else countText = `${totalCount.toLocaleString(locale)} registro${totalCount === 1 ? "" : "s"}`;

  const renderBody = () => {
    if (!loading && unread) {
      return <EmptyState>A leitura falhou. Nada aqui está sendo mostrado como vazio por esse motivo.</EmptyState>;
    }
    if (!loading && filteredLogs.length === 0) return <EmptyState>Nenhum registro encontrado.</EmptyState>;
    return (
      <table style={table}>
        <thead>
          <tr>
            {HEADERS.map((h) => (
              <th key={h} style={th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: 8 }, (_, i) => (
                <tr key={i}>
                  {HEADERS.map((h) => (
                    <td key={h} style={td}><SkeletonLine width={`${55 + (i % 4) * 8}%`} /></td>
                  ))}
                </tr>
              ))
            : filteredLogs.map((log) => (
                <LogRow
                  key={log.id}
                  log={log}
                  lookups={lookups}
                  locale={locale}
                  expanded={expanded.has(log.id)}
                  onToggle={toggleExpand}
                />
              ))}
        </tbody>
      </table>
    );
  };

  return (
    <div>
      <div style={countLine}>{countText}</div>

      <div style={filters}>
        <select value={filterValues.action} onChange={(e) => setFilter("action", e.target.value)} style={input}>
          <option value="">Todas as acoes</option>
          {actionOptions.map((a) => (
            <option key={a} value={a}>{labelForAuditAction(a)}</option>
          ))}
        </select>
        <select value={filterValues.origin} onChange={(e) => setFilter("origin", e.target.value)} style={input}>
          <option value="">Todas as origens</option>
          {AUDIT_ORIGIN_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={filterValues.entity}
          onChange={(e) => setFilter("entity", e.target.value)}
          style={input}
          aria-label="Entidade"
        >
          <option value="">Todas as entidades</option>
          {entityOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Filtrar por staff..."
          value={staffFilter}
          onChange={(e) => setStaffFilter(e.target.value)}
          style={{ ...input, minWidth: 160 }}
        />
        <input type="date" value={filterValues.dateFrom} onChange={(e) => setFilter("dateFrom", e.target.value)} style={input} />
        <input type="date" value={filterValues.dateTo} onChange={(e) => setFilter("dateTo", e.target.value)} style={input} />
      </div>

      <div style={wrap}>{renderBody()}</div>

      {totalPages > 1 && (
        <div style={pagination}>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ ...pageBtn, opacity: page === 0 ? 0.4 : 1 }}
          >
            Anterior
          </button>
          <span style={pageInfo}>Pagina {page + 1} de {totalPages}</span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{ ...pageBtn, opacity: page >= totalPages - 1 ? 0.4 : 1 }}
          >
            Proxima
          </button>
        </div>
      )}
    </div>
  );
}

async function fetchFilterOptions(
  tenantId: string,
): Promise<{ actions: string[]; entities: { value: string; label: string }[] }> {
  const actions = new Set<string>();
  const entities = new Set<string>();
  for (let p = 0; p < OPTION_MAX_PAGES; p++) {
    const { data, error } = await supabase
      .from("audit_log")
      .select("action, entity_type")
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false })
      .range(p * OPTION_PAGE, p * OPTION_PAGE + OPTION_PAGE - 1);
    if (error || !data) break;
    for (const row of data as { action: string | null; entity_type: string | null }[]) {
      if (row.action) actions.add(row.action);
      if (row.entity_type) entities.add(row.entity_type);
    }
    if (data.length < OPTION_PAGE) break;
  }
  return {
    actions: Array.from(actions, (value) => ({ value, label: labelForAuditAction(value) }))
      .sort((a, b) => PT_BR.compare(a.label, b.label))
      .map((a) => a.value),
    entities: auditEntityOptions(entities),
  };
}

async function fetchLogs(tenantId: string, f: LogFilters, page: number): Promise<Omit<LogPage, "key">> {
  let query = supabase
    .from("audit_log")
    .select("*", { count: "exact" })
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
  if (f.action) query = query.eq("action", f.action);
  if (f.origin) query = query.eq("actor_type", f.origin);
  if (f.entity) query = query.eq("entity_type", f.entity);
  if (f.dateFrom) query = query.gte("created_at", `${f.dateFrom}T00:00:00`);
  if (f.dateTo) query = query.lte("created_at", `${f.dateTo}T23:59:59`);
  query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

  const { data, count, error } = await query;
  if (error || !data) return { rows: [], total: 0, unread: true };
  return { rows: data as AuditLogRow[], total: count ?? 0, unread: false };
}

interface LogRowProps {
  log: AuditLogRow;
  lookups: Lookups;
  locale: string;
  expanded: boolean;
  onToggle: (id: string) => void;
}

const LogRow = memo(function LogRow({ log, lookups, locale, expanded, onToggle }: LogRowProps) {
  const summary = summarizeAuditRow(log, lookups);
  const actionLabel = labelForAuditRow(log.action, log.details, {
    grantLastDay: revokedGrantLastDay(log, lookups),
  });

  return (
    <Fragment>
      <tr>
        <td style={td}>
          {new Date(log.created_at).toLocaleString(locale, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </td>
        <td style={td}>{log.staff_id ? staffName(lookups, log.staff_id) ?? "—" : "—"}</td>
        <td style={td}><span style={entPill}>{labelForAuditOrigin(log.actor_type)}</span></td>
        <td style={td}><span style={pill}>{actionLabel}</span></td>
        <td style={td}><span style={entPill}>{labelForAuditEntity(log.entity_type)}</span></td>
        <td style={td}><AboutCell summary={summary} /></td>
        <td style={td}>
          {hasDetails(log.details) ? (
            <button type="button" onClick={() => onToggle(log.id)} style={linkBtn}>
              {expanded ? "Ocultar" : "Ver"}
            </button>
          ) : (
            <span style={{ color: colors.stone }}>{"—"}</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={HEADERS.length} style={detailsCell}>
            <pre style={detailsJson}>{JSON.stringify(log.details, null, 2)}</pre>
          </td>
        </tr>
      )}
    </Fragment>
  );
});

function AboutCell({ summary }: { summary: AuditSummary }) {
  const pending = typeof summary.primary !== "string";
  const gone = summary.targetState !== "ok";
  const secondary = [
    ...(gone && summary.rawId ? [<span key="id" style={monoId}>{`${summary.rawId}…`}</span>] : []),
    ...summary.secondary,
  ];

  return (
    <div style={about} aria-busy={pending || undefined} data-testid="audit-about">
      {typeof summary.primary === "string" ? (
        <div style={gone ? { ...aboutPrimary, ...goneText } : aboutPrimary} title={summary.primary}>
          {summary.primary}
        </div>
      ) : (
        <div style={aboutSkeleton}><SkeletonLine width="60%" height={10} /></div>
      )}
      {secondary.length > 0 && (
        <div style={aboutSecondary}>
          {secondary.map((fragment, i) => (
            <Fragment key={i}>
              {i > 0 && <span aria-hidden="true">{" · "}</span>}
              {fragment}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

/** Staff column name; undefined while unresolved or not found, so callers keep their fallback. */
function staffName(lookups: Lookups, id: string): string | undefined {
  if (!lookups.profiles.has(id)) return undefined;
  const profile = lookups.profiles.get(id);
  if (!profile) return undefined;
  return profile.full_name || "Desconhecido";
}

function hasDetails(details: unknown): boolean {
  if (details === null || details === undefined) return false;
  if (typeof details === "object") return Object.keys(details).length > 0;
  return true;
}

const HEADERS = ["Data", "Staff", "Origem", "Ação", "Entidade", "Sobre", ""] as const;

const countLine: CSSProperties = {
  fontSize: fontSize.md,
  color: colors.stone,
  marginBottom: space.lg,
};

const filters: CSSProperties = {
  display: "flex",
  gap: space.sm,
  flexWrap: "wrap",
  marginBottom: space.lg,
};

const input: CSSProperties = {
  padding: "8px 10px",
  border: `1px solid ${colors.fog}`,
  borderRadius: radius.sm,
  background: colors.white,
  color: colors.ink,
  fontSize: fontSize.md,
};

const wrap: CSSProperties = {
  background: colors.white,
  border: `1px solid ${colors.fog}`,
  borderRadius: radius.md,
  overflowX: "auto",
  boxShadow: shadow.xs,
};

const table: CSSProperties = {
  width: "100%",
  minWidth: 900,
  borderCollapse: "collapse",
  fontSize: fontSize.md,
};

const th: CSSProperties = {
  textAlign: "left",
  fontSize: fontSize.xs,
  fontWeight: fontWeight.semibold,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: colors.stone,
  padding: "12px 16px",
  background: colors.paper,
  borderBottom: `1px solid ${colors.fog}`,
};

const td: CSSProperties = {
  padding: "14px 16px",
  borderBottom: `1px solid ${colors.fog}`,
  color: colors.ink,
  verticalAlign: "top",
  whiteSpace: "nowrap",
};

const pill: CSSProperties = {
  display: "inline-block",
  fontSize: fontSize.xs,
  padding: "2px 8px",
  borderRadius: radius.pill,
  background: colors.indigoSoft,
  color: colors.indigo,
  fontWeight: fontWeight.medium,
};

const entPill: CSSProperties = { ...pill, background: colors.fog, color: colors.stone };

// Browsers ignore min/max width on a td, so the Sobre width lives on this wrapper.
const about: CSSProperties = {
  minWidth: 280,
  maxWidth: 380,
  whiteSpace: "normal",
};

const aboutPrimary: CSSProperties = {
  color: colors.ink,
  fontWeight: fontWeight.medium,
  lineHeight: 1.35,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const aboutSkeleton: CSSProperties = {
  padding: `${space.xs}px 0`,
};

const goneText: CSSProperties = {
  color: colors.stoneSoft,
  fontStyle: "italic",
  fontWeight: fontWeight.regular,
};

const aboutSecondary: CSSProperties = {
  color: colors.stone,
  fontSize: fontSize.sm,
  lineHeight: 1.4,
  marginTop: 2,
};

const monoId: CSSProperties = {
  fontFamily: fontFamilyMono,
  fontSize: fontSize.sm,
  color: colors.stoneSoft,
};

const linkBtn: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: colors.indigo,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.medium,
  cursor: "pointer",
  fontFamily: "inherit",
};

const detailsCell: CSSProperties = {
  padding: "0 16px 16px",
  borderBottom: `1px solid ${colors.fog}`,
};

const detailsJson: CSSProperties = {
  background: colors.paper,
  border: `1px solid ${colors.fog}`,
  borderRadius: radius.sm,
  padding: "12px 16px",
  color: colors.ink,
  fontSize: fontSize.sm,
  fontFamily: "ui-monospace, monospace",
  overflow: "auto",
  maxHeight: 300,
  margin: 0,
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
};

const pagination: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: space.lg,
  marginTop: space.xl,
};

const pageBtn: CSSProperties = {
  padding: "8px 18px",
  background: colors.paper,
  border: `1px solid ${colors.fog}`,
  borderRadius: radius.sm,
  color: colors.ink,
  fontSize: fontSize.md,
  cursor: "pointer",
  fontWeight: fontWeight.medium,
};

const pageInfo: CSSProperties = {
  color: colors.stone,
  fontSize: fontSize.md,
};
